import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulb} from '@fortawesome/free-solid-svg-icons/faLightbulb';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons/faPaperPlane';

const styles = {
  container: {
    padding: '16px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#f8f9fa'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#666'
  },
  suggestionsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  suggestionButton: {},
  suggestionButtonHover: {
    borderColor: '#0b93f6',
    backgroundColor: '#f0f8ff',
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  suggestionText: {
    flex: 1,
    marginRight: '8px'
  },
  suggestionIcon: {
    opacity: 0.6,
    fontSize: '12px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '20px',
    color: '#999',
    fontStyle: 'italic'
  }
};

const PromptSuggestions = ({
  suggestions = [],
  onSuggestionClick,
  title = 'Suggested prompts',
  showIcon = true,
  disabled = false,
  maxVisible = null,
  layout = 'list' // 'list' or 'grid'
}) => {
  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  const visibleSuggestions = maxVisible ? suggestions.slice(0, maxVisible) : suggestions;

  const handleSuggestionClick = suggestion => {
    if (disabled || !onSuggestionClick) return;

    // Handle both string suggestions and object suggestions
    const promptText = typeof suggestion === 'string' ? suggestion : suggestion.text || suggestion.prompt;
    onSuggestionClick(promptText, suggestion);
  };

  const gridStyle =
    layout === 'grid'
      ? {
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '8px'
        }
      : styles.suggestionsGrid;

  return (
    <div style={styles.container}>
      {showIcon && (
        <div style={styles.header}>
          <FontAwesomeIcon icon={faLightbulb} />
          {title}
        </div>
      )}

      <div style={gridStyle}>
        {visibleSuggestions.map((suggestion, index) => {
          const suggestionText = typeof suggestion === 'string' ? suggestion : suggestion.text || suggestion.prompt;
          const suggestionId = typeof suggestion === 'object' ? suggestion.id || index : index;

          return (
            <button
              key={suggestionId}
              onClick={() => handleSuggestionClick(suggestion)}
              title={suggestionText}
              className="vm-suggestion-button"
            >
              <span style={styles.suggestionText}>{suggestionText}</span>
              <FontAwesomeIcon icon={faPaperPlane} style={styles.suggestionIcon} />
            </button>
          );
        })}
      </div>

      {maxVisible && suggestions.length > maxVisible && (
        <div style={{ marginTop: '8px', textAlign: 'center' }}>
          <small style={{ color: '#666' }}>{suggestions.length - maxVisible} more suggestions available...</small>
        </div>
      )}
    </div>
  );
};

PromptSuggestions.propTypes = {
  suggestions: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        text: PropTypes.string,
        prompt: PropTypes.string,
        category: PropTypes.string
      })
    ])
  ),
  onSuggestionClick: PropTypes.func.isRequired,
  title: PropTypes.string,
  showIcon: PropTypes.bool,
  disabled: PropTypes.bool,
  maxVisible: PropTypes.number,
  layout: PropTypes.oneOf(['list', 'grid'])
};

export default PromptSuggestions;
